import { toASCII } from 'punycode';
import slugify from '@nettbureau/slugify';
import { BACKEND_URL } from '../config';
import { Catalog, Chain, Person } from '../queries/types';
import { TBackground } from '../schema/image-schema';

export function buildStaticPath(parts: string[]) {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return buildPunycodeSlug(parts[0]!) + normalizeUrl(...parts.slice(1));
}

export function normalizeUrl(...parts: (string | undefined | null)[]) {
  const result = parts.reduce((previous, current) => {
    if (!current) return previous;

    return `${previous}${buildSlug(current)}`;
  }, '');

  if (!result) {
    throw new Error('We should never get here');
  }

  return result;
}

export function getSlug(slug: string | undefined | null, fallback: string) {
  if (slug && slug.length > 0) {
    return slug;
  }

  return fallback;
}

export function buildUrl(parts: string[]): string {
  if (parts.length < 1) {
    throw new Error('Invalid paths array');
  }

  const base = isDev() ? '/' : 'https://';

  return parts.reduce((acc, part, index) => {
    if (index === 0) {
      return acc + buildPunycodeSlug(part);
    }

    return acc + normalizeUrl(part);
  }, base);
}

export function buildImageUrl(image: TBackground | null) {
  return image?.data?.attributes.url && BACKEND_URL
    ? buildImageUrlFromString(image.data.attributes.url)
    : undefined;
}

export function openUrl(url: string, openTab: boolean) {
  if (openTab) {
    const tab = window.open(url, '_blank');
    if (tab === null) return;
    tab.focus();

    return;
  }

  window.location.href = url;
}

export function slots(people: Person[], catalog: Catalog) {
  const peopleIds = people.map((person) => person.id);

  const chains = catalog.chains;

  const companiesWithoutChain = catalog.companies.filter((company) =>
    chains.every((chain) => !chain.companies.find((c) => company.id === c.id)),
  );

  const peopleByChain = chains
    .map((chain: Chain) => {
      const uniquePeople = new Map<number, Person>();

      chain.companies.forEach((company) => {
        company.people.forEach((person) => {
          if (!uniquePeople.has(person.id) && peopleIds.includes(person.id)) {
            uniquePeople.set(person.id, person);
          }
        });
      });

      return Array.from(uniquePeople.values());
    })
    .filter((personGroup) => personGroup.length > 0);

  const peopleByCompany = companiesWithoutChain
    .map((company) => {
      const uniquePeople = new Map<number, Person>();

      company.people.forEach((person) => {
        if (!uniquePeople.has(person.id) && peopleIds.includes(person.id)) {
          uniquePeople.set(person.id, person);
        }
      });

      return Array.from(uniquePeople.values());
    })
    .filter((personGroup) => personGroup.length > 0);

  return [...peopleByChain, ...peopleByCompany];
}

export function buildImageUrlFromString(url: string) {
  return BACKEND_URL ? `https://fatcamp.io/${BACKEND_URL}${url}` : undefined;
}

/** Builds a slug without the leading slash. */
export function buildSlug(input: string) {
  return slugify(input);
}

export function buildPunycodeSlug(input: string) {
  return toASCII(input.toLowerCase());
}

export function unWrapNull<T>(input: T | null | undefined) {
  return input === null ? undefined : input;
}

export function isDev() {
  return process.env.NODE_ENV === 'development';
}

export function addUtmParams(url: string, source: string) {
  const [base = '', query = ''] = url.split('?');

  const searchParams = new URLSearchParams(query);

  ['utm_source', 'utm_medium', 'utm_campaign'].forEach((param) =>
    searchParams.delete(param),
  );

  searchParams.set('utm_source', source.toLowerCase());
  searchParams.set('utm_medium', 'nettbureau');
  searchParams.set('utm_campaign', 'company_page');

  const queryString = searchParams.toString();

  return queryString.length > 0 ? `${base}?${queryString}` : base;
}
